import { useRef } from "react";
import type { KeyboardEvent } from "react";
import { cn } from "@/lib/utils";
import type { Option } from "@/services/multi-agent/protocol";

/**
 * The ≤4 ranked options of an interaction, as a vertical radio group (contract §5.4 `clarify`).
 *
 * WHY NOT `SegmentedControl`, WHICH IS THIS REPO'S RADIOGROUP. That control is a horizontal pill and
 * says so in its own header: options "never wrap inside the pill — a two-line segmented control stops
 * reading as one control". These options carry a `description` as sub-copy and run up to four, so
 * they need a stacked card each. The a11y CONTRACT is reused verbatim — `role="radiogroup"` with an
 * `aria-label`, `role="radio"` + `aria-checked` per option, arrow keys moving the selection, and a
 * roving `tabIndex` so Tab leaves the whole group rather than walking every option. Copying the
 * pattern rather than the component is the reuse that applies here.
 *
 * THE RECOMMENDED OPTION IS PRE-SELECTED AND NEVER AUTO-SUBMITTED (§5.4). Selection lives with the
 * caller precisely so this component cannot submit: it has no submit handler to call.
 *
 * `recommended` is shown as a text badge, not as colour alone — §16's rule that state never rests on
 * colour, and the reason a "Recommended" word appears next to the label instead of a tinted border.
 */
export interface OptionCardsProps {
  /** Names the group for assistive tech. Required: an unlabelled radiogroup announces nothing. */
  ariaLabel: string;
  options: readonly Option[];
  /** `null` renders nothing selected — a legitimate state when the server recommends no option. */
  value: string | null;
  onChange: (optionId: string) => void;
  disabled?: boolean;
  /** Prefix for each option's DOM id, so the caller can point `aria-describedby` at one. */
  idPrefix: string;
  /**
   * `stack` (default): one card per option, room for `description` sub-copy.
   * `row`: pills in a wrapping row, for options that are bare labels — "Approve · Edit first ·
   * Reject" does not need three full-width rows. The a11y contract is identical in both.
   */
  layout?: "stack" | "row";
  /** Options shown but not selectable (e.g. an Approve the server would refuse). Never skipped silently. */
  disabledOptionIds?: readonly string[];
}

export function OptionCards({
  ariaLabel,
  options,
  value,
  onChange,
  disabled,
  idPrefix,
  layout = "stack",
  disabledOptionIds,
}: OptionCardsProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = (event: KeyboardEvent, index: number) => {
    const delta =
      event.key === "ArrowDown" || event.key === "ArrowRight"
        ? 1
        : event.key === "ArrowUp" || event.key === "ArrowLeft"
          ? -1
          : 0;
    if (!delta || options.length === 0) return;
    event.preventDefault();
    let next = (index + delta + options.length) % options.length;
    for (let i = 0; i < options.length && disabledOptionIds?.includes(options[next].option_id); i++) {
      next = (next + delta + options.length) % options.length;
    }
    if (disabledOptionIds?.includes(options[next].option_id)) return;
    onChange(options[next].option_id);
    refs.current[next]?.focus();
  };

  if (!options.length) return null;

  /*
   * Which option Tab lands on. With nothing selected the FIRST option is tabbable — otherwise the
   * whole group is unreachable by keyboard, since every option would carry `tabIndex={-1}`.
   */
  const selectedIndex = options.findIndex((o) => o.option_id === value);
  const firstEnabled = options.findIndex((o) => !disabledOptionIds?.includes(o.option_id));
  const focusIndex = selectedIndex >= 0 ? selectedIndex : Math.max(0, firstEnabled);

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(layout === "row" ? "flex flex-wrap gap-1.5" : "flex flex-col gap-1.5", disabled && "pointer-events-none opacity-60")}
    >
      {options.map((option, index) => {
        const isActive = option.option_id === value;
        const optionDisabled = !!disabledOptionIds?.includes(option.option_id);
        return (
          <button
            key={option.option_id}
            id={`${idPrefix}-${option.option_id}`}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isActive}
            tabIndex={index === focusIndex ? 0 : -1}
            disabled={disabled || optionDisabled}
            aria-disabled={disabled || optionDisabled || undefined}
            data-option-unavailable={optionDisabled ? "" : undefined}
            onClick={() => !optionDisabled && onChange(option.option_id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            /*
             * LIGHTER INK. Every option was a filled, bordered card, so three choices meant three
             * heavy rectangles inside what should read as a message — the "form dropped into a chat"
             * look, in the one place the reader most needs to think rather than to fill something in.
             *
             * The unselected rows now carry no border and no fill and rely on hover; the SELECTED one
             * keeps its border and tint. The state is still on three channels — the filled dot,
             * `aria-checked`, and the selected row's border — so nothing rests on colour alone (§16),
             * and the row that matters is the only one drawn.
             */
            className={cn(
              "text-left transition-colors motion-reduce:transition-none",
              optionDisabled && "cursor-not-allowed opacity-50",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
              layout === "row"
                ? // A pill: bordered whether or not selected, so the row reads as a set of choices
                  // rather than loose words; the selected one is the tinted, primary-bordered pill.
                  "inline-flex max-w-full items-center rounded-full border px-3 py-1.5"
                : "w-full rounded-md border px-3 py-2",
              isActive
                ? "border-primary bg-primary/5"
                : layout === "row"
                  ? "border-border hover:bg-muted/40"
                  : "border-transparent hover:bg-muted/40",
            )}
          >
            <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              {/*
                The selected state is carried by a filled dot AND by `aria-checked` AND by the border
                — three channels, none of them colour alone.
              */}
              <span
                aria-hidden
                className={cn(
                  "mt-[3px] inline-block h-3 w-3 shrink-0 self-start rounded-full border",
                  isActive ? "border-primary bg-primary" : "border-muted-foreground/50",
                )}
              />
              <span className={cn("min-w-0 text-[13px] font-medium text-foreground", layout === "row" ? "truncate" : "flex-1")}>
                {option.label}
              </span>
              {option.recommended && (
                <span
                  className={cn(
                    "shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground",
                    // In a pill the word sits inline after the label; in a card it is a badge.
                    layout === "row" ? "" : "rounded bg-muted px-1.5 py-0.5",
                  )}
                >
                  Recommended
                </span>
              )}
            </span>
            {option.description && (
              // Sub-copy, per §5.4. Indented to the label, not the dot, so the two read as one block.
              <span className="mt-1 block pl-5 text-[12px] leading-snug text-muted-foreground">
                {option.description}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
